/**
 * WheelEngine - Core Canvas Wheel Spinner
 * wheel.expert - Enterprise Spin the Wheel
 * 
 * ALGORITHM: Uses Web Crypto API (crypto.getRandomValues) for 
 * cryptographically secure random number generation.
 * This ensures true randomness superior to Math.random().
 */

// Singleton AudioContext to prevent memory leaks
let sharedAudioContext = null;

const getAudioContext = () => {
  if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
    sharedAudioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  // Resume if suspended (browser autoplay policy)
  if (sharedAudioContext.state === 'suspended') {
    sharedAudioContext.resume();
  }
  return sharedAudioContext;
};

class WheelEngine {
  constructor(canvas, options = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.isDestroyed = false; // Flag to track if the wheel has been destroyed

    // Configuration
    this.config = {
      entries: options.entries || [],
      colors: options.colors || this.defaultColors,
      textColor: options.textColor || '#FFFFFF',
      fontSize: options.fontSize || 16,
      fontFamily: options.fontFamily || 'Inter, Arial, sans-serif',
      spinDuration: options.spinDuration || 5000,
      minSpins: options.minSpins || 5,
      maxSpins: options.maxSpins || 8,
      pointerAngle: options.pointerAngle || 270,
      sounds: options.sounds !== false,
      onSpinStart: options.onSpinStart || (() => {}),
      onSpinEnd: options.onSpinEnd || (() => {}),
      onTick: options.onTick || (() => {}),
    };

    // State
    this.rotation = 0;
    this.spinning = false;
    this.currentEntry = null;
    this.animationId = null;
    this.lastSpinTime = 0;

    // Initialize
    this.init();
  }

  get defaultColors() {
    return [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
      '#F8B500', '#00D4AA', '#FF8C42', '#6C5CE7', '#A8E6CF'
    ];
  }

  init() {
    this.setupCanvas();
    this.draw();
  }

  setupCanvas() {
    // Skip if wheel is destroyed or canvas is not available
    if (this.isDestroyed || !this.canvas || !this.canvas.getContext) {
      return;
    }
    
    try {
      const dpr = window.devicePixelRatio || 1;
      const rect = this.canvas.getBoundingClientRect();
      
      // Ensure minimum dimensions to prevent negative radius
      const minSize = 100;
      const width = Math.max(rect.width || minSize, minSize);
      const height = Math.max(rect.height || minSize, minSize);
      
      // Try to set canvas dimensions - this will throw if canvas was transferred
      try {
        this.canvas.width = width * dpr;
        this.canvas.height = height * dpr;
      } catch (canvasError) {
        // Canvas has been transferred to offscreen, mark as destroyed
        this.isDestroyed = true;
        this.canvas = null;
        this.ctx = null;
        return;
      }
      
      this.ctx.scale(dpr, dpr);
      this.displayWidth = width;
      this.displayHeight = height;
      this.centerX = width / 2;
      this.centerY = height / 2;
      // Ensure radius is always positive and at least 30
      this.radius = Math.max(Math.min(this.centerX, this.centerY) - 10, 30);
    } catch (e) {
      // Canvas may have been transferred to offscreen or is in invalid state
      this.isDestroyed = true;
      this.canvas = null;
      this.ctx = null;
    }
  }

  playSound(name) {
    if (!this.config.sounds) return;
    
    const style = this.config.soundStyle || 'classic';
    
    try {
      const ctx = getAudioContext();
      
      // Sound configurations per style
      const soundConfigs = {
        classic: {
          tick: { freq: 800, type: 'sine', gain: 0.08, duration: 0.03 },
          spinStart: { freq: 300, type: 'sine', gain: 0.15, duration: 0.2 },
          win: { notes: [523.25, 659.25, 783.99, 1046.50], type: 'sine', gain: 0.15 }
        },
        casino: {
          tick: { freq: 1200, type: 'square', gain: 0.05, duration: 0.02 },
          spinStart: { freq: 200, type: 'sawtooth', gain: 0.12, duration: 0.3 },
          win: { notes: [587.33, 739.99, 880, 1174.66], type: 'square', gain: 0.12 }
        },
        arcade: {
          tick: { freq: 1500, type: 'square', gain: 0.06, duration: 0.015 },
          spinStart: { freq: 150, type: 'sawtooth', gain: 0.1, duration: 0.15 },
          win: { notes: [440, 554.37, 659.25, 880], type: 'square', gain: 0.1 }
        },
        soft: {
          tick: { freq: 600, type: 'sine', gain: 0.04, duration: 0.05 },
          spinStart: { freq: 250, type: 'sine', gain: 0.08, duration: 0.25 },
          win: { notes: [392, 493.88, 587.33, 783.99], type: 'sine', gain: 0.1 }
        }
      };
      
      const config = soundConfigs[style] || soundConfigs.classic;
      
      if (name === 'tick') {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.frequency.value = config.tick.freq;
        oscillator.type = config.tick.type;
        gainNode.gain.setValueAtTime(config.tick.gain, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + config.tick.duration);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + config.tick.duration);
      } else if (name === 'spinStart') {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.frequency.value = config.spinStart.freq;
        oscillator.type = config.spinStart.type;
        gainNode.gain.setValueAtTime(config.spinStart.gain, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + config.spinStart.duration);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + config.spinStart.duration);
      } else if (name === 'win') {
        // Victory sound based on victorySound setting
        const victoryType = this.config.victorySound || 'fanfare';
        this.playVictorySound(ctx, victoryType);
      }
    } catch (e) {
      // Audio not supported - fail silently
      console.debug('[WheelEngine] Audio not available:', e.message);
    }
  }

  playVictorySound(ctx, type) {
    // Enhanced victory sounds with professional audio design
    const victorySounds = {
      fanfare: {
        // Epic triumphant fanfare - rich brass chord progression
        // Uses major chord voicings for maximum triumph feel
        notes: [
          { freq: 349.23, delay: 0, dur: 0.3 },    // F4
          { freq: 440.00, delay: 0, dur: 0.3 },    // A4 (major third)
          { freq: 523.25, delay: 0, dur: 0.3 },    // C5 (fifth)
          { freq: 523.25, delay: 0.15, dur: 0.25 }, // C5 flourish
          { freq: 659.25, delay: 0.25, dur: 0.25 }, // E5
          { freq: 698.46, delay: 0.4, dur: 0.4 },  // F5 resolution
          { freq: 880.00, delay: 0.4, dur: 0.4 },  // A5 
          { freq: 1046.50, delay: 0.4, dur: 0.5 }, // C6 finale
        ],
        type: 'sawtooth',
        gain: 0.10,
        useChord: true
      },
      applause: {
        // Realistic crowd applause with multiple texture layers
        useNoise: true,
        duration: 2.0,
        gain: 0.18,
        layers: 3,
        frequencyBands: [1500, 2500, 4000]
      },
      cheers: {
        // Excited crowd cheers with voice-like formants
        notes: [
          { freq: 280, delay: 0, dur: 0.2 },
          { freq: 350, delay: 0.05, dur: 0.2 },
          { freq: 420, delay: 0.1, dur: 0.25 },
          { freq: 380, delay: 0.2, dur: 0.2 },
          { freq: 450, delay: 0.25, dur: 0.3 },
          { freq: 500, delay: 0.35, dur: 0.25 },
        ],
        type: 'sawtooth',
        gain: 0.08,
        addNoise: true,
        noiseGain: 0.12,
        useFormants: true
      },
      coins: {
        // Casino jackpot coin cascade - bright metallic sounds
        notes: [
          { freq: 2400, delay: 0, dur: 0.12 },
          { freq: 2800, delay: 0.04, dur: 0.10 },
          { freq: 2200, delay: 0.08, dur: 0.12 },
          { freq: 3000, delay: 0.12, dur: 0.10 },
          { freq: 2600, delay: 0.16, dur: 0.12 },
          { freq: 2000, delay: 0.20, dur: 0.14 },
          { freq: 3200, delay: 0.24, dur: 0.10 },
          { freq: 2400, delay: 0.28, dur: 0.12 },
          { freq: 2800, delay: 0.32, dur: 0.10 },
          { freq: 2100, delay: 0.36, dur: 0.15 },
          { freq: 3400, delay: 0.42, dur: 0.12 },
          { freq: 2500, delay: 0.48, dur: 0.18 },
        ],
        type: 'sine',
        gain: 0.07,
        useResonance: true,
        resonanceQ: 30
      },
      magic: {
        // Ethereal magical sparkle with shimmer effect
        notes: [
          { freq: 880, delay: 0, dur: 0.2 },
          { freq: 1100, delay: 0.06, dur: 0.18 },
          { freq: 1320, delay: 0.12, dur: 0.16 },
          { freq: 1540, delay: 0.18, dur: 0.14 },
          { freq: 1760, delay: 0.24, dur: 0.14 },
          { freq: 2000, delay: 0.30, dur: 0.12 },
          { freq: 2200, delay: 0.36, dur: 0.12 },
          { freq: 2640, delay: 0.42, dur: 0.20 },
        ],
        type: 'sine',
        gain: 0.06,
        vibrato: true,
        vibratoRate: 8,
        vibratoDepth: 20,
        shimmer: true
      },
      simple: {
        // Clean pleasant success chime
        notes: [
          { freq: 880, delay: 0, dur: 0.4 },     // A5
          { freq: 1108.73, delay: 0.12, dur: 0.35 }, // C#6
          { freq: 1318.51, delay: 0.24, dur: 0.5 },  // E6
        ],
        type: 'sine',
        gain: 0.12,
        useReverb: true
      }
    };

    const sound = victorySounds[type] || victorySounds.fanfare;

    if (sound.useNoise) {
      // Enhanced applause with multiple texture layers
      const layers = sound.layers || 1;
      const bands = sound.frequencyBands || [2000];
      
      bands.forEach((bandFreq, layerIndex) => {
        const bufferSize = ctx.sampleRate * sound.duration;
        const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        
        for (let i = 0; i < bufferSize; i++) {
          // Create varied burst pattern for realistic applause
          const progress = i / bufferSize;
          const envelope = Math.sin(Math.PI * progress) * 
                          (1 + 0.4 * Math.sin(i * 0.008 + layerIndex)) *
                          (1 + 0.2 * Math.sin(i * 0.02 + layerIndex * 2));
          // Add some randomness variation
          const burst = Math.random() > 0.85 ? 1.3 : 1;
          data[i] = (Math.random() * 2 - 1) * envelope * burst;
        }
        
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        
        const gainNode = ctx.createGain();
        gainNode.gain.setValueAtTime(sound.gain / layers, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + sound.duration);
        
        // Bandpass filter for texture
        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.value = bandFreq;
        filter.Q.value = 0.8;
        
        source.connect(filter);
        filter.connect(gainNode);
        gainNode.connect(ctx.destination);
        source.start(ctx.currentTime + layerIndex * 0.02);
      });
    } else {
      // Enhanced note sequence with new format
      sound.notes.forEach((note) => {
        const freq = typeof note === 'object' ? note.freq : note;
        const delay = typeof note === 'object' ? note.delay : 0;
        const dur = typeof note === 'object' ? note.dur : 0.2;
        
        const osc = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        // Optional resonance filter for metallic sounds (coins)
        let outputNode = gainNode;
        if (sound.useResonance) {
          const resonanceFilter = ctx.createBiquadFilter();
          resonanceFilter.type = 'bandpass';
          resonanceFilter.frequency.value = freq;
          resonanceFilter.Q.value = sound.resonanceQ || 20;
          osc.connect(resonanceFilter);
          resonanceFilter.connect(gainNode);
        } else {
          osc.connect(gainNode);
        }
        gainNode.connect(ctx.destination);
        
        osc.frequency.value = freq;
        osc.type = sound.type;
        
        // Enhanced vibrato with shimmer
        if (sound.vibrato) {
          const vibratoOsc = ctx.createOscillator();
          const vibratoGain = ctx.createGain();
          vibratoOsc.frequency.value = sound.vibratoRate || 6;
          vibratoGain.gain.value = sound.vibratoDepth || 15;
          vibratoOsc.connect(vibratoGain);
          vibratoGain.connect(osc.frequency);
          vibratoOsc.start(ctx.currentTime + delay);
          vibratoOsc.stop(ctx.currentTime + delay + dur + 0.1);
        }
        
        // Shimmer effect - adds subtle high harmonics
        if (sound.shimmer) {
          const shimmerOsc = ctx.createOscillator();
          const shimmerGain = ctx.createGain();
          shimmerOsc.frequency.value = freq * 2;
          shimmerOsc.type = 'sine';
          shimmerGain.gain.setValueAtTime(sound.gain * 0.15, ctx.currentTime + delay);
          shimmerGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + dur * 0.8);
          shimmerOsc.connect(shimmerGain);
          shimmerGain.connect(ctx.destination);
          shimmerOsc.start(ctx.currentTime + delay);
          shimmerOsc.stop(ctx.currentTime + delay + dur);
        }
        
        const startTime = ctx.currentTime + delay;
        // Smooth attack and release envelope
        gainNode.gain.setValueAtTime(0.001, startTime);
        gainNode.gain.exponentialRampToValueAtTime(sound.gain, startTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + dur);
        
        osc.start(startTime);
        osc.stop(startTime + dur + 0.05);
      });
      
      // Enhanced noise layer for cheers
      if (sound.addNoise) {
        const noiseDuration = 0.8;
        const noiseBuffer = ctx.createBuffer(1, ctx.sampleRate * noiseDuration, ctx.sampleRate);
        const noiseData = noiseBuffer.getChannelData(0);
        for (let i = 0; i < noiseBuffer.length; i++) {
          const progress = i / noiseBuffer.length;
          const envelope = Math.sin(Math.PI * progress) * (1 + 0.3 * Math.sin(i * 0.015));
          noiseData[i] = (Math.random() * 2 - 1) * envelope;
        }
        const noiseSource = ctx.createBufferSource();
        noiseSource.buffer = noiseBuffer;
        const noiseGain = ctx.createGain();
        const noiseLevel = sound.noiseGain || 0.08;
        noiseGain.gain.setValueAtTime(noiseLevel, ctx.currentTime);
        noiseGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + noiseDuration);
        
        // Add formant filter for voice-like quality
        if (sound.useFormants) {
          const formantFilter = ctx.createBiquadFilter();
          formantFilter.type = 'bandpass';
          formantFilter.frequency.value = 1200;
          formantFilter.Q.value = 2;
          noiseSource.connect(formantFilter);
          formantFilter.connect(noiseGain);
        } else {
          noiseSource.connect(noiseGain);
        }
        noiseGain.connect(ctx.destination);
        noiseSource.start(ctx.currentTime);
      }
    }
  }

  setVictorySound(sound) {
    this.config.victorySound = sound;
  }

  setSoundStyle(style) {
    this.config.soundStyle = style;
  }

  setOnSpinEnd(callback) {
    this.config.onSpinEnd = callback || (() => {});
  }

  setEntries(entries) {
    // Skip if wheel is destroyed or canvas is invalid
    if (this.isDestroyed || !this.canvas || !this.ctx) {
      return;
    }
    this.config.entries = entries.map((entry, index) => ({
      text: typeof entry === 'string' ? entry : entry.text,
      color: entry.color || this.config.colors[index % this.config.colors.length],
      weight: entry.weight || 1,
      id: entry.id || `entry-${index}-${Date.now()}`
    }));
    this.draw();
  }

  draw() {
    // Skip if wheel is destroyed or canvas context is not available
    if (this.isDestroyed || !this.ctx || !this.canvas) {
      return;
    }
    
    try {
      const { ctx, centerX, centerY, config, displayWidth, displayHeight } = this;
      const entries = config.entries;
      
      // Ensure radius is always valid before drawing
      const radius = Math.max(this.radius || 30, 30);

      // Clear canvas
      ctx.clearRect(0, 0, displayWidth, displayHeight);

      if (entries.length === 0) {
        this.drawEmptyWheel();
        return;
      }

      // Special case: only 1 entry - show full circle
      if (entries.length === 1) {
        this.drawSingleEntryWheel(entries[0]);
        return;
      }

      // Calculate total weight
      const totalWeight = entries.reduce((sum, e) => sum + e.weight, 0);

      // Draw shadow
      ctx.save();
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 20;
      ctx.shadowOffsetY = 5;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fillStyle = '#FFF';
      ctx.fill();
      ctx.restore();

      // Draw wheel
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(this.rotation);

      let startAngle = 0;
      entries.forEach((entry, index) => {
        const sliceAngle = (entry.weight / totalWeight) * Math.PI * 2;

        // Draw slice
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.arc(0, 0, radius, startAngle, startAngle + sliceAngle);
        ctx.closePath();
        ctx.fillStyle = entry.color;
        ctx.fill();

        // Slice border
        ctx.strokeStyle = 'rgba(255,255,255,0.4)';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Draw text
        ctx.save();
        const midAngle = startAngle + sliceAngle / 2;
        ctx.rotate(midAngle);
        
        // Adjust font size based on number of entries and slice size
        const baseFontSize = Math.max(10, Math.min(config.fontSize, radius / entries.length * 1.5));
        const fontSize = Math.max(10, baseFontSize);
        ctx.font = `bold ${fontSize}px ${config.fontFamily}`;
        
        // Truncate text if needed
        const maxWidth = radius - 50;
        let text = entry.text;
        while (ctx.measureText(text).width > maxWidth && text.length > 3) {
          text = text.slice(0, -4) + '...';
        }
        
        // Add text shadow for better readability
        ctx.shadowColor = 'rgba(0,0,0,0.3)';
        ctx.shadowBlur = 2;
        ctx.fillStyle = config.textColor;
        
        // Check if text should be flipped (when in bottom half of wheel)
        // Normalize angle to 0-2π range
        const normalizedAngle = ((midAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        
        if (normalizedAngle > Math.PI / 2 && normalizedAngle < Math.PI * 1.5) {
          // Bottom half - flip text
          ctx.rotate(Math.PI);
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(text, -(radius - 25), 0);
        } else {
          // Top half - normal text
          ctx.textAlign = 'right';
          ctx.textBaseline = 'middle';
          ctx.fillText(text, radius - 25, 0);
        }
        ctx.restore();

        startAngle += sliceAngle;
      });
      ctx.restore();

      // Draw outer ring
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(0,0,0,0.1)';
      ctx.lineWidth = 4;
      ctx.stroke();

      // Draw center circle
      this.drawCenter();

      // Draw pointer
      this.drawPointer();
    } catch (e) {
      // Canvas may be in invalid state - silently skip
    }
  }

  drawSingleEntryWheel(entry) {
    if (!this.ctx || !this.canvas) return;
    try {
      const { ctx, centerX, centerY, radius, config } = this;

      // Draw shadow
      ctx.save();
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 20;
      ctx.shadowOffsetY = 5;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fillStyle = entry.color;
      ctx.fill();
      ctx.restore();

      // Draw text in center
      ctx.fillStyle = config.textColor;
      ctx.font = `bold 20px ${config.fontFamily}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.3)';
      ctx.shadowBlur = 2;
      ctx.fillText(entry.text, centerX, centerY - 50);
      
      // Draw message
      ctx.font = `14px ${config.fontFamily}`;
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.fillText('🎉 Winner!', centerX, centerY + 50);

      this.drawCenter();
      this.drawPointer();
    } catch (e) {
      // Canvas invalid - skip
    }
  }

  drawEmptyWheel() {
    if (!this.ctx || !this.canvas) return;
    try {
      const { ctx, centerX, centerY, radius } = this;

      // Draw empty circle
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
      gradient.addColorStop(0, '#F8FAFC');
      gradient.addColorStop(1, '#E2E8F0');
      ctx.fillStyle = gradient;
      ctx.fill();
      ctx.strokeStyle = '#CBD5E1';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Draw placeholder text
      ctx.fillStyle = '#94A3B8';
      ctx.font = 'bold 16px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      // Draw placeholder text
      ctx.fillStyle = '#94A3B8';
      ctx.font = 'bold 16px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Add entries to spin', centerX, centerY - 10);
      ctx.font = '14px Inter, Arial, sans-serif';
      ctx.fillText('✨', centerX, centerY + 15);

      this.drawCenter();
      this.drawPointer();
    } catch (e) {
      // Canvas invalid - skip
    }
  }

  drawCenter() {
    if (!this.ctx || !this.canvas) return;
    try {
      const { ctx, centerX, centerY } = this;

      // Outer ring
      ctx.beginPath();
      ctx.arc(centerX, centerY, 40, 0, Math.PI * 2);
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();
      ctx.strokeStyle = '#E5E7EB';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Inner circle with gradient
      ctx.beginPath();
      ctx.arc(centerX, centerY, 30, 0, Math.PI * 2);
      const gradient = ctx.createRadialGradient(centerX - 5, centerY - 5, 0, centerX, centerY, 30);
      gradient.addColorStop(0, '#60A5FA');
      gradient.addColorStop(1, '#2563EB');
      ctx.fillStyle = gradient;
      ctx.fill();

      // Center dot
      ctx.beginPath();
      ctx.arc(centerX, centerY, 8, 0, Math.PI * 2);
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();
    } catch (e) {
      // Canvas invalid - skip
    }
  }

  drawPointer() {
    if (!this.ctx || !this.canvas) return;
    try {
      const { ctx, centerX, centerY, radius } = this;
      const pointerSize = 35;

      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate((this.config.pointerAngle * Math.PI) / 180);
      ctx.translate(0, -radius + 5);

      // Pointer shadow
      ctx.shadowColor = 'rgba(0,0,0,0.3)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetY = 3;

      // Pointer shape
      ctx.beginPath();
      ctx.moveTo(0, pointerSize);
      ctx.lineTo(-18, -8);
      ctx.lineTo(18, -8);
      ctx.closePath();
      
      const gradient = ctx.createLinearGradient(0, -8, 0, pointerSize);
      gradient.addColorStop(0, '#EF4444');
      gradient.addColorStop(1, '#DC2626');
      ctx.fillStyle = gradient;
      ctx.fill();
      
      ctx.strokeStyle = '#B91C1C';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.restore();
    } catch (e) {
      // Canvas invalid - skip
    }
  }

  /**
   * Cryptographically Secure Random Number Generator
   * Uses Web Crypto API (crypto.getRandomValues) which provides
   * cryptographically strong random values from the browser's
   * entropy source, superior to Math.random().
   * 
   * @returns {number} Random float between 0 (inclusive) and 1 (exclusive)
   */
  getSecureRandom() {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return array[0] / (0xFFFFFFFF + 1);
  }

  /**
   * Unbiased random integer selection using rejection sampling
   * Eliminates modulo bias for fair selection
   * 
   * @param {number} max - Maximum value (exclusive)
   * @returns {number} Random integer from 0 to max-1
   */
  getUnbiasedRandomInt(max) {
    if (max <= 0) return 0;
    if (max === 1) return 0;
    
    // Calculate the largest multiple of max that fits in 32 bits
    const maxValid = Math.floor(0xFFFFFFFF / max) * max;
    
    let result;
    const array = new Uint32Array(1);
    
    // Rejection sampling: reject values that would cause bias
    do {
      crypto.getRandomValues(array);
      result = array[0];
    } while (result >= maxValid);
    
    return result % max;
  }

  /**
   * Check if wheel can be spun
   * @returns {boolean} True if spin is allowed
   */
  canSpin() {
    // Must have at least 2 entries for meaningful spin
    if (this.config.entries.length < 2) return false;
    // Cannot spin while already spinning
    if (this.spinning) return false;
    // Prevent rapid consecutive spins (min 500ms between spins)
    if (Date.now() - this.lastSpinTime < 500) return false;
    return true;
  }

  /**
   * Get reason why spin is not allowed
   * @returns {string|null} Reason or null if spin is allowed
   */
  getSpinBlockReason() {
    if (this.config.entries.length === 0) return 'noEntries';
    if (this.config.entries.length === 1) return 'singleEntry';
    if (this.spinning) return 'alreadySpinning';
    if (Date.now() - this.lastSpinTime < 500) return 'tooFast';
    return null;
  }

  spin() {
    if (!this.canSpin()) {
      const reason = this.getSpinBlockReason();
      console.debug('[WheelEngine] Spin blocked:', reason);
      return null;
    }

    this.spinning = true;
    this.lastSpinTime = Date.now();
    this.config.onSpinStart();
    this.playSound('spinStart');

    // Select winner using appropriate method based on weights
    const entries = this.config.entries;
    const allEqualWeight = entries.every(e => e.weight === entries[0].weight);
    
    let selectedIndex;
    
    if (allEqualWeight) {
      // All weights equal: use unbiased rejection sampling for perfect fairness
      selectedIndex = this.getUnbiasedRandomInt(entries.length);
    } else {
      // Weighted selection: use crypto-secure random with weighted distribution
      const totalWeight = entries.reduce((sum, e) => sum + e.weight, 0);
      let random = this.getSecureRandom() * totalWeight;
      selectedIndex = 0;
      for (let i = 0; i < entries.length; i++) {
        random -= entries[i].weight;
        if (random <= 0) {
          selectedIndex = i;
          break;
        }
      }
    }

    // Calculate total weight for angle calculations
    const totalWeight = entries.reduce((sum, e) => sum + e.weight, 0);

    // Calculate target rotation
    // Canvas coordinate system: 0° = right (3 o'clock), angles increase CLOCKWISE
    // The pointer is at the LEFT side of the wheel (π radians = 180°)
    // 
    // When ctx.rotate(R) is applied, a slice drawn at angle θ
    // APPEARS at visual angle (θ + R) from the observer's perspective.
    // 
    // To align slice center (θ) with pointer (targetAngle):
    //   θ + R = targetAngle
    //   R = targetAngle - θ
    
    // Calculate where the selected slice is drawn (before rotation)
    let cumulativeWeight = 0;
    for (let i = 0; i < selectedIndex; i++) {
      cumulativeWeight += this.config.entries[i].weight;
    }
    
    // Slice angles (clockwise from right = 0)
    const sliceStartAngle = (cumulativeWeight / totalWeight) * 2 * Math.PI;
    const sliceAngle = (this.config.entries[selectedIndex].weight / totalWeight) * 2 * Math.PI;
    const sliceCenterAngle = sliceStartAngle + sliceAngle / 2;
    
    // The pointer visually points to the LEFT (π radians = 180° = 9 o'clock)
    const targetAngle = Math.PI;
    
    // Calculate rotation needed: R = targetAngle - sliceCenterAngle
    // Adjust for current rotation position
    let rotationNeeded = targetAngle - sliceCenterAngle - (this.rotation % (2 * Math.PI));
    
    // Normalize to ensure we always spin forward (positive direction)
    while (rotationNeeded <= 0) rotationNeeded += 2 * Math.PI;
    
    // Add random number of full spins
    const spins = this.config.minSpins + Math.floor(this.getSecureRandom() * (this.config.maxSpins - this.config.minSpins + 1));
    const targetRotation = this.rotation + rotationNeeded + spins * 2 * Math.PI;

    // Animate with optimized performance
    const startRotation = this.rotation;
    const startTime = performance.now();
    let lastTickAngle = startRotation;
    let lastTickSlice = -1;

    const animate = (currentTime) => {
      // Stop animation if wheel is destroyed or canvas is no longer valid
      if (this.isDestroyed || !this.canvas || !this.ctx) {
        this.spinning = false;
        return;
      }
      
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / this.config.spinDuration, 1);

      // Easing: easeOutQuint for more pronounced deceleration at the end
      // This creates that "almost stopping but keeps going" effect like competitors
      const eased = 1 - Math.pow(1 - progress, 5);
      this.rotation = startRotation + (targetRotation - startRotation) * eased;

      // Optimized tick sound - only play when crossing slice boundary
      const entriesCount = this.config.entries.length;
      const sliceSize = (Math.PI * 2) / entriesCount;
      const normalizedRotation = ((this.rotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      const currentSlice = Math.floor(normalizedRotation / sliceSize);
      
      // Play tick sounds more prominently near the end for suspense
      if (currentSlice !== lastTickSlice && progress < 0.95) {
        this.playSound('tick');
        this.config.onTick();
        lastTickSlice = currentSlice;
      }

      this.draw();

      if (progress < 1) {
        this.animationId = requestAnimationFrame(animate);
      } else {
        this.spinning = false;
        this.currentEntry = this.config.entries[selectedIndex];
        this.playSound('win');
        this.config.onSpinEnd(this.currentEntry);
      }
    };

    this.animationId = requestAnimationFrame(animate);
    return this.config.entries[selectedIndex];
  }

  stop() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.spinning = false;
  }

  reset() {
    this.stop();
    this.rotation = 0;
    this.currentEntry = null;
    this.draw();
  }

  getResult() {
    return this.currentEntry;
  }

  removeEntry(id) {
    this.config.entries = this.config.entries.filter(e => e.id !== id);
    this.draw();
  }

  shuffle() {
    // Use secure random for shuffle
    const entries = [...this.config.entries];
    for (let i = entries.length - 1; i > 0; i--) {
      const j = Math.floor(this.getSecureRandom() * (i + 1));
      [entries[i], entries[j]] = [entries[j], entries[i]];
    }
    this.config.entries = entries;
    this.draw();
  }

  setColors(colors) {
    this.config.colors = colors;
    this.config.entries.forEach((entry, i) => {
      entry.color = colors[i % colors.length];
    });
    this.draw();
  }

  setSounds(enabled) {
    this.config.sounds = enabled;
  }

  resize() {
    if (!this.canvas || !this.ctx) {
      return;
    }
    this.setupCanvas();
    this.draw();
  }

  destroy() {
    this.isDestroyed = true;
    this.stop();
    // Clear references to allow garbage collection
    this.canvas = null;
    this.ctx = null;
  }
}

export default WheelEngine;
